// Pricing data for Serene Sparkle
// Used across Contact form, Packages page, and Admin dashboard

export const PRICING = {
  residential: {
    label: 'Residential Cleaning',
    services: [
      {
        id: 'standard-home-cleaning',
        name: 'Standard Home Cleaning',
        description: 'General cleaning: dusting, vacuuming, mopping, kitchen & bathroom surfaces',
        type: 'hourly',
        minPrice: 18,
        maxPrice: 30,
        unit: 'per hour per cleaner',
      },
      {
        id: 'deep-cleaning',
        name: 'Deep Cleaning',
        description: 'Detailed top-to-bottom clean including inside appliances, skirting boards, and hard-to-reach areas',
        type: 'custom',
        minPrice: 120,
        maxPrice: 250,
        unit: 'depending on job size',
      },
      {
        id: 'end-of-tenancy',
        name: 'End of Tenancy Cleaning',
        description: 'Full deep clean for move-out inspection, including oven, windows, and bathrooms',
        type: 'custom',
        minPrice: 150,
        maxPrice: 500,
        unit: 'depending on property size',
      },
      {
        id: 'move-in-out',
        name: 'Move-In / Move-Out Cleaning',
        description: 'Pre/post move thorough cleaning for new tenants or homeowners',
        type: 'custom',
        minPrice: 140,
        maxPrice: 280,
        unit: 'depending on job size',
      },
      {
        id: 'one-off-cleaning',
        name: 'One-Off Cleaning',
        description: 'Flexible, no-contract cleaning session',
        type: 'hourly',
        minPrice: 20,
        maxPrice: 30,
        unit: 'per hour',
      },
      {
        id: 'eco-friendly-cleaning',
        name: 'Eco-Friendly Cleaning',
        description: 'Using non-toxic and sustainable cleaning products',
        type: 'addon',
        minPrice: 10,
        maxPrice: 20,
        unit: 'per service (premium option)',
      },
    ],
  },
  commercial: {
    label: 'Commercial Cleaning',
    services: [
      {
        id: 'office-cleaning',
        name: 'Office Cleaning (Regular)',
        description: 'Routine cleaning of desks, floors, and communal areas',
        type: 'hourly',
        minPrice: 20,
        maxPrice: 25,
        unit: 'per hour',
      },
      {
        id: 'small-business-retail',
        name: 'Small Business / Retail Cleaning',
        description: 'Cleaning for shops, salons, or clinics',
        type: 'hourly',
        minPrice: 25,
        maxPrice: 30,
        unit: 'per hour',
      },
      {
        id: 'after-hours-cleaning',
        name: 'After-Hours Cleaning',
        description: 'Evening or weekend service for minimal disruption',
        type: 'hourly',
        minPrice: 22,
        maxPrice: 33,
        unit: 'per hour (+10% premium)',
      },
      {
        id: 'contract-cleaning',
        name: 'Contract Cleaning (Weekly/Monthly)',
        description: 'Tailored cleaning schedules for ongoing business clients',
        type: 'custom',
        minPrice: 350,
        maxPrice: 1200,
        unit: 'depending on frequency & size',
      },
    ],
  },
  specialty: {
    label: 'Specialty Cleaning',
    services: [
      {
        id: 'post-construction',
        name: 'Post-Construction / Renovation Cleaning',
        description: 'Heavy-duty cleaning after building or decorating work',
        type: 'custom',
        minPrice: 180,
        maxPrice: 400,
        unit: 'depending on job size',
      },
      {
        id: 'carpet-upholstery',
        name: 'Carpet & Upholstery Cleaning',
        description: 'Deep steam cleaning for carpets, sofas, and chairs',
        type: 'custom',
        minPrice: 50,
        maxPrice: 150,
        unit: 'depending on job size',
      },
      {
        id: 'event-after-party',
        name: 'Event / After-Party Cleaning',
        description: 'Full clean-up including waste removal and sanitizing',
        type: 'custom',
        minPrice: 100,
        maxPrice: 250,
        unit: 'depending on job size',
      },
      {
        id: 'disinfection-sanitization',
        name: 'Disinfection & Sanitization',
        description: 'High-level disinfecting for hygiene-critical areas',
        type: 'custom',
        minPrice: 80,
        maxPrice: 250,
        unit: 'depending on job size',
      },
      {
        id: 'power-wash-cleaning',
        name: 'Power Wash Cleaning',
        description: 'Driveway cleaning, conservatory cleaning, pavement cleaning, pool areas cleaning',
        type: 'custom',
        minPrice: 60,
        maxPrice: 400,
        unit: 'depending on job size',
      },
      {
        id: 'landscaping',
        name: 'Landscaping',
        description: 'Garden maintenance, planting, lawn care, and outdoor space transformations',
        type: 'custom',
        minPrice: 0,
        maxPrice: 0,
        unit: 'quoted individually',
      },
      {
        id: 'tree-surgery',
        name: 'Tree Surgery',
        description: 'Professional tree pruning, trimming, removal, and stump grinding',
        type: 'custom',
        minPrice: 0,
        maxPrice: 0,
        unit: 'quoted individually',
      },
      {
        id: 'custom-special',
        name: 'Special Requests (Custom)',
        description: 'Tailored packages for unique cleaning needs',
        type: 'custom',
        minPrice: 0,
        maxPrice: 0,
        unit: 'quoted individually',
      },
    ],
  },
};

// Flat list of all services for dropdowns
export const ALL_SERVICES = Object.values(PRICING).flatMap(cat =>
  cat.services.map(s => ({ ...s, category: cat.label }))
);

// Get service by ID
export const getServiceById = (id) => ALL_SERVICES.find(s => s.id === id);

// Format price range
export const formatPriceRange = (service) => {
  if (service.minPrice === 0 && service.maxPrice === 0) return 'Quoted individually';
  if (service.type === 'addon') return `+£${service.minPrice} – £${service.maxPrice}`;
  if (service.type === 'custom') return `From £${service.minPrice}`;
  return `£${service.minPrice} – £${service.maxPrice}`;
};

// Calculate estimate for hourly services
export const calculateEstimate = (service, hours) => {
  if (!service || service.type !== 'hourly' || !hours) return null;
  return {
    min: service.minPrice * hours,
    max: service.maxPrice * hours,
  };
};
